import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/layout/Header';
import CategoryNavigation from '../components/common/CategoryNavigation';
import ProgressBar from '../components/common/ProgressBar';
import NavigationButtons from '../components/common/NavigationButtons';
import BackgroundQuestions from '../components/categories/BackgroundQuestions';
import IndustryQuestions from '../components/categories/IndustryQuestions';
import LearningQuestions from '../components/categories/LearningQuestions';
import LearningEnvironmentQuestions from '../components/categories/LearningEnvironmentQuestions';
import SocialBehaviorQuestions from '../components/categories/SocialBehaviorQuestions';
import DecisionMakingQuestions from '../components/categories/DecisionMakingQuestions';
import AchievementSatisfactionQuestions from '../components/categories/AchievementSatisfactionQuestions';
import AdaptabilityQuestions from '../components/categories/AdaptabilityQuestions';
import PersonalInfoQuestions from '../components/categories/PersonalInfoQuestions';
import { X, Info } from 'lucide-react';

const QuestionPage = ({ 
  categories,
  currentStep,
  animateDirection,
  categoryInfo,
  progress,
  nextStep,
  prevStep,
  backToReview,
  hasCompletedOnce,
  isEditingFromReview,
  totalSteps,
  navigateToCategory
}) => {
  const [showJumpPopup, setShowJumpPopup] = useState(false);
  const previousStepRef = useRef(0);
  const popupShownRef = useRef(false);
  
  // Track step changes to detect auto-jump
  useEffect(() => {
    // Only show popup if:
    // 1. We jumped from a low step to a higher step (auto-navigation)
    // 2. We haven't shown the popup before
    // 3. Not editing from review
    // 4. Current step is greater than 1
    if (
      currentStep > 1 && 
      previousStepRef.current <= 1 && 
      !popupShownRef.current && 
      !isEditingFromReview
    ) {
      console.log("Showing jump popup", { 
        currentStep, 
        previousStep: previousStepRef.current,
        popupShown: popupShownRef.current
      });
      
      setShowJumpPopup(true);
      popupShownRef.current = true; // Mark that we've shown the popup
      
      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setShowJumpPopup(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
    
    // Update previous step for next comparison
    previousStepRef.current = currentStep;
  }, [currentStep, isEditingFromReview]);
  
  // Determine which category we're in
  const renderCategoryQuestion = () => {
    const { categoryIndex, questionInCategory, totalQuestionsInCategory } = categoryInfo;

    if (categoryIndex === 0) {
      return (
        <PersonalInfoQuestions
          questionNumber={questionInCategory}
          categories={categories}
          totalQuestions={totalQuestionsInCategory}
        />
      );
    } else if (categoryIndex === 1) {
      return (
        <BackgroundQuestions
          questionNumber={questionInCategory}
          categories={categories}
          totalQuestions={totalQuestionsInCategory}
        />
      );
    } else if (categoryIndex === 2) {
      return (
        <IndustryQuestions
          questionNumber={questionInCategory}
          categories={categories}
          totalQuestions={totalQuestionsInCategory}
        />
      );
    } else if (categoryIndex === 3) {
      return (
        <LearningQuestions
          questionNumber={questionInCategory}
          categories={categories}
          totalQuestions={totalQuestionsInCategory}
        />
      );
    } else if (categoryIndex === 4) {
      return (
        <LearningEnvironmentQuestions
          questionNumber={questionInCategory}
          categories={categories}
          totalQuestions={totalQuestionsInCategory}
        />
      );
    } else if (categoryIndex === 5) {
      return (
        <SocialBehaviorQuestions
          questionNumber={questionInCategory}
          categories={categories}
          totalQuestions={totalQuestionsInCategory}
        />
      );
    } else if (categoryIndex === 6) {
      return (
        <DecisionMakingQuestions
          questionNumber={questionInCategory}
          categories={categories}
          totalQuestions={totalQuestionsInCategory}
        />
      );
    } else if (categoryIndex === 7) {
      return (
        <AchievementSatisfactionQuestions
          questionNumber={questionInCategory}
          categories={categories}
          totalQuestions={totalQuestionsInCategory}
        />
      );
    } else if (categoryIndex === 8) {
      return (
        <AdaptabilityQuestions
          questionNumber={questionInCategory}
          categories={categories}
          totalQuestions={totalQuestionsInCategory}
        />
      );
    }

    return null;
  };
  
  // Inline CSS for the popup animation
  const popupAnimation = {
    animation: 'fadeIn 0.3s ease-out forwards'
  };
  
  // Keyframes for the animation are added with a style tag directly to the component
  const keyframesStyle = `
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;
  
  return (
    <>
      <Header />
      
      {/* Add keyframes style */}
      <style>{keyframesStyle}</style>
      
      {/* Jump notification - Popup/Modal */}
      {showJumpPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          {/* Semi-transparent backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowJumpPopup(false)}
          />
          
          {/* Popup content */}
          <div 
            className="bg-white rounded-xl shadow-lg p-6 max-w-md m-4 relative z-10"
            style={popupAnimation}
          >
            <div className="flex items-start mb-4">
              <div className="bg-blue-100 p-3 rounded-full mr-4">
                <Info className="h-6 w-6 text-blue-600" />
              </div>
              
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">正在继续您的评估</h3>
                <p className="text-gray-600">
                  我们帮您跳转到了您上次填写到的位置。您可以使用上方的导航查看或编辑之前的问题。
                </p>
              </div>
              
              <button 
                onClick={() => setShowJumpPopup(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mt-4">
              <button
                onClick={() => setShowJumpPopup(false)}
                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                明白了
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Category Navigation */}
      <CategoryNavigation 
        categories={categories} 
        categoryInfo={categoryInfo} 
        onCategoryClick={navigateToCategory}
      />
      
      {/* Progress bar */}
      <ProgressBar 
        progress={progress} 
        currentStep={currentStep}
        totalSteps={totalSteps}
        categoryInfo={categoryInfo}
        categories={categories}
      />
      
      {/* Main content area */}
      <div className="w-full max-w-2xl relative overflow-hidden">
        <div 
          className={`transition-all duration-300 ease-in-out transform ${
            animateDirection === 'next-out' ? 'translate-x-4 opacity-0' : 
            animateDirection === 'prev-out' ? '-translate-x-4 opacity-0' : 
            animateDirection === 'next-in' ? 'translate-x-0 opacity-100' : 
            animateDirection === 'prev-in' ? 'translate-x-0 opacity-100' : 
            'translate-x-0 opacity-100'
          }`}
        >
          {renderCategoryQuestion()}
        </div>
      </div>
      
      {/* Navigation buttons */}
      <NavigationButtons 
        currentStep={currentStep}
        totalSteps={totalSteps}
        prevStep={prevStep}
        nextStep={nextStep}
        backToReview={backToReview}
        hasCompletedOnce={hasCompletedOnce}
        isEditingFromReview={isEditingFromReview}
      />
    </>
  );
};

export default QuestionPage;